import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  ModalBody,
  ModalContent,
  ModalFooter,
  Skeleton,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  ChainFullIds,
  type ChainId,
  ChainIds,
  type MultichainTokenBalance,
  formatCurrency,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  type UnifiedBalanceWithPrices,
  useCurrentAccount,
  useGetChainBalance,
  useGetNetworkChain,
  useGetNetworkSymbol,
  useGetTokensAndBalances,
  useSelectedNetwork,
} from "@umami/state";
import {
  BridgeDirection,
  type GaslessQuoteResponse,
  type LiFiQuoteResponse,
  type ZeroXQuoteResponse,
} from "@umami/swap";
import { calculateDirection } from "@umami/swap/src/services/layerZeroBridgeQuote";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { SwapInfo } from "./SwapInfo";
import { TokenIconWrapper } from "../IconWrapper";
import { QuoteManager, type QuoteWithProvider } from "./providers";
import { SwapValidator } from "./validation";
import { FormPageHeader } from "../SendFlow/FormPageHeader";
import { type FormPageProps, FormSubmitButton, formDefaultValues } from "../SendFlow/utils";
import { ChainIcon } from "../Token/ChainIcon";

import { ChevronDownIcon, SwapIcon, WalletIcon } from "@/assets/icons";
import { useFastWithdrawalRates } from "@/hooks/useFastWithdrawalRates";
import { useColor } from "@/styles/useColor";

export type FormValues = {
  sellAmount: string;
  buyAmount: string;
  chainId: ChainId;
  buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
  sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
  slippage: number;
};

export const FormPage = (
  props: FormPageProps<FormValues> & {
    onPreviewClick: (params: {
      sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
      buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
      sellAmount: string;
      buyAmount: string;
      buyFiatAmount: string;
      fiatValue: string;
      sellFiatAmount: string;
      slippage: number;
      zeroXQuote?: ZeroXQuoteResponse | null;
      gaslessQuote?: GaslessQuoteResponse | null;
      lifiQuote?: LiFiQuoteResponse | null;
      selectedQuote?: QuoteWithProvider | null;
      isGaslessSwap?: boolean;
      isFastWithdrawal?: boolean;
    }) => void;
    onSelectSellTokenClick?: (params: {
      buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    }) => void;
    onSelectBuyTokenClick?: (params: {
      sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    }) => void;
  }
) => {
  const color = useColor();
  const toast = useToast();
  const { formValues } = useDynamicModalContext();
  const currentAccount = useCurrentAccount()!;
  const currentNetwork = useSelectedNetwork();
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const { getTokenUsdPrice } = useGetTokensAndBalances();
  const getChainBalance = useGetChainBalance();
  const [feeEstimate, setFeeEstimate] = useState<{
    nativeFee: bigint;
    zroFee: bigint;
    nativeFees?: Array<{
      amount: bigint;
      symbol: string;
      chainId?: string;
    }>;
  } | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<QuoteWithProvider | null>(null);
  const [isGaslessSwap, setIsGaslessSwap] = useState(false);
  const [slippage, setSlippage] = useState(0.5);
  const fetchQuoteRef = useRef<typeof fetchQuote | null>(null);
  const bridgingEffectRunRef = useRef(false);

  const [sellToken, setSellToken] = useState(formValues.sellToken);
  const [buyToken, setBuyToken] = useState(formValues.buyToken);

  const getNetworkSymbol = useGetNetworkSymbol();
  const getNetworkChain = useGetNetworkChain();

  const sellTokenChainId: ChainId = sellToken?.chainId && getNetworkChain(sellToken.chainId);
  const buyTokenChainId: ChainId = buyToken?.chainId && getNetworkChain(buyToken.chainId);
  const [isFastWithdrawal, setIsFastWithdrawal] = useState(false);

  const canShowFastWithdrawal =
    sellTokenChainId === ChainIds.ETHERLINK &&
    buyTokenChainId === ChainIds.TEZOS &&
    sellToken?.symbol === "XTZ" &&
    buyToken?.symbol === "XTZ";

  const {
    min: fastWithdrawalMin,
    max: fastWithdrawalMax,
    rate: fastWithdrawalRate,
    loading: fastWithdrawalLoading,
    error: fastWithdrawalError,
  } = useFastWithdrawalRates(canShowFastWithdrawal);

  const quoteManager = useMemo(() => new QuoteManager(), []);

  const isNativeTokenSwap = useMemo(
    () => SwapValidator.isNativeTokenSwap(sellToken, buyToken),
    [sellToken, buyToken]
  );

  const isBridgingSameTokenOnDifferentChains = useMemo(
    () => SwapValidator.isBridgingSameTokenOnDifferentChains(sellToken, buyToken, getNetworkChain),
    [sellToken, buyToken, getNetworkChain]
  );

  const isTezosEtherlinkSwap = useMemo(
    () => SwapValidator.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain),
    [sellToken, buyToken, getNetworkChain]
  );

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      ...formDefaultValues(props),
      chainId: formValues.tokenChain,
      sellAmount: formValues.sellAmount || "",
      buyAmount: formValues.buyAmount || "",
    },
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
  } = form;

  const sellAmount = watch("sellAmount");
  const buyAmount = watch("buyAmount");
  const canShowSwapInfo = sellToken?.chainId && buyToken?.chainId && sellAmount && buyAmount;

  const conversionRate = useMemo(() => {
    if (!selectedQuote || !sellToken || !buyToken) {
      return 1;
    }

    try {
      const sellAmountInTokens = parseFloat(selectedQuote.sellAmount);
      const buyAmountInTokens = parseFloat(selectedQuote.buyAmount);

      if (buyAmountInTokens === 0) {
        return 1; // Avoid division by zero
      }

      return sellAmountInTokens / buyAmountInTokens;
    } catch (error) {
      console.error("Error calculating conversion rate:", error);

      return 1; // Fallback to 1 if calculation fails
    }
  }, [selectedQuote, sellToken, buyToken]);

  const fastWithdrawalBuyAmount = useMemo(() => {
    if (
      isFastWithdrawal &&
      fastWithdrawalRate !== null &&
      sellAmount &&
      !isNaN(Number(sellAmount))
    ) {
      return (Number(sellAmount) * (1 - fastWithdrawalRate)).toFixed(6);
    }

    if (isTezosEtherlinkSwap) {
      return sellAmount;
    }

    return buyAmount;
  }, [isFastWithdrawal, fastWithdrawalRate, sellAmount, buyAmount, isTezosEtherlinkSwap]);

  useEffect(() => {
    if (fastWithdrawalBuyAmount !== buyAmount) {
      setValue("buyAmount", fastWithdrawalBuyAmount);
    }
  }, [fastWithdrawalBuyAmount, buyAmount, setValue]);

  const fetchQuote = useCallback(
    async (
      sellAmount?: string,
      buyAmount?: string,
      currentSellToken?: typeof sellToken,
      currentBuyToken?: typeof buyToken
    ) => {
      // Use passed tokens or fall back to state tokens
      const sellTokenToUse = currentSellToken || sellToken;
      const buyTokenToUse = currentBuyToken || buyToken;

      if (!sellAmount && !buyAmount) {
        return;
      }

      if (!sellTokenToUse?.address) {
        console.error("Missing sell token address:", sellTokenToUse);

        return;
      }

      if (!buyTokenToUse?.address) {
        console.error("Missing buy token address:", buyTokenToUse);

        return;
      }

      setIsLoadingQuote(true);
      try {
        if (!currentAccount.chains?.[ChainFullIds.ETHERLINK]?.address.pkh) {
          return;
        }

        const { quotes, errors } = await quoteManager.fetchAllQuotes({
          sellToken: sellTokenToUse,
          buyToken: buyTokenToUse,
          sellAmount: sellAmount || "",
          userAddress: currentAccount.chains[ChainFullIds.ETHERLINK]?.address.pkh ?? "",
          network: currentNetwork.name,
          slippage,
          isGaslessSwap,
          getNetworkChain,
        });

        if (errors.length > 0) {
          const errorMessage =
            errors.length === 1 ? errors[0] : `Some quotes failed: ${errors.join(", ")}`;
          toast({
            title: "Quote fetch errors",
            description: errorMessage,
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }

        if (quotes.length > 0) {
          const bestQuote = quoteManager.selectBestQuote(
            quotes,
            buyTokenToUse,
            getTokenUsdPrice,
            getNetworkChain
          );

          if (bestQuote) {
            setSelectedQuote(bestQuote);

            if (sellAmount) {
              setValue("buyAmount", bestQuote.buyAmount);
              void trigger("buyAmount");
            } else if (buyAmount) {
              setValue("sellAmount", bestQuote.sellAmount);
              void trigger("sellAmount");
            }
          }
        } else if (errors.length > 0) {
          // Reset buy amount when there are no successful quotes
          if (sellAmount) {
            setValue("buyAmount", "");
          }
        }
      } catch (error) {
        console.error("Error fetching quotes:", error);
        toast({
          title: "Failed to fetch quotes",
          description: error instanceof Error ? error.message : "Failed to fetch quotes",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        // Reset buy amount when there's an error
        if (sellAmount) {
          setValue("buyAmount", "");
        }
      } finally {
        setIsLoadingQuote(false);
      }
    },
    [
      sellToken,
      buyToken,
      setValue,
      toast,
      currentNetwork.name,
      currentAccount.chains,
      isGaslessSwap,
      trigger,
      quoteManager,
      getTokenUsdPrice,
      slippage,
      getNetworkChain,
    ]
  );

  useEffect(() => {
    fetchQuoteRef.current = fetchQuote;
  }, [fetchQuote]);

  const debouncedFetchQuote = useMemo(
    () =>
      debounce((sellAmount?: string, buyAmount?: string) => {
        if (fetchQuoteRef.current) {
          void fetchQuoteRef.current(sellAmount, buyAmount);
        }
      }, 1000),
    []
  );

  // Sync initial amounts when bridging the same token - only run once on mount
  useEffect(() => {
    // Only run this effect once
    if (bridgingEffectRunRef.current) {
      return;
    }

    bridgingEffectRunRef.current = true;

    if (isBridgingSameTokenOnDifferentChains) {
      if (sellAmount && !buyAmount) {
        setValue("buyAmount", sellAmount);
      } else if (buyAmount && !sellAmount) {
        setValue("sellAmount", buyAmount);
      }
    } else {
      if (sellAmount && !buyAmount) {
        void fetchQuote(sellAmount, undefined);
      } else if (buyAmount && !sellAmount) {
        void fetchQuote(undefined, buyAmount);
      }
    }
  }, [fetchQuote, isBridgingSameTokenOnDifferentChains, setValue, sellAmount, buyAmount]);

  const handleSellAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue("sellAmount", newValue);

    if (!isFastWithdrawal && !isTezosEtherlinkSwap) {
      debouncedFetchQuote(newValue, undefined);
    }

    void trigger("sellAmount");
  };

  const handleSlippageChange = (value: number) => {
    setSlippage(value);
  };

  const handleFeeEstimateChange = useCallback(
    (
      feeEstimate: {
        nativeFee: bigint;
        zroFee: bigint;
        nativeFees?: Array<{
          amount: bigint;
          symbol: string;
          chainId?: string;
        }>;
      } | null
    ) => {
      setFeeEstimate(feeEstimate);
    },
    []
  );

  const handleGaslessSwapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setIsGaslessSwap(newValue);

    // Immediately refetch quote with new mode if we have amounts
    if (sellAmount || buyAmount) {
      // Use a timeout to ensure state is updated
      setTimeout(() => {
        if (fetchQuoteRef.current) {
          void fetchQuoteRef.current(sellAmount, buyAmount);
        }
      }, 0);
    }
  };

  const handleSwapTokensClick = () => {
    if (!sellToken || !buyToken) {
      return;
    }

    setValue("sellAmount", buyAmount || "");
    setSellToken(buyToken);
    setBuyToken(sellToken);

    void trigger(["sellAmount", "buyAmount"]);

    void fetchQuote(buyAmount || "", "", buyToken, sellToken);
  };

  const validationParams = useMemo(
    () => ({
      sellToken,
      buyToken,
      sellAmount,
      feeEstimate,
      isGaslessSwap,
      nativeBalance: sellToken?.chainId ? getChainBalance(sellToken.chainId) : { balance: "0" },
      nativeSymbol: sellToken?.chainId ? getNetworkSymbol(sellToken.chainId) : "XTZ",
      getNetworkChain,
      getChainBalance,
    }),
    [
      sellToken,
      buyToken,
      sellAmount,
      feeEstimate,
      isGaslessSwap,
      getChainBalance,
      getNetworkSymbol,
      getNetworkChain,
    ]
  );

  const isSellAmountExceedingBalance = useCallback(
    () => SwapValidator.isSellAmountExceedingBalance(validationParams),
    [validationParams]
  );

  const isNetworkFeeExceedingNativeBalance = useCallback(
    () => SwapValidator.isNetworkFeeExceedingNativeBalance(validationParams),
    [validationParams]
  );

  const isSellAmountOnlyExceedingBalance = useCallback(
    () => SwapValidator.isSellAmountOnlyExceedingBalance(validationParams),
    [validationParams]
  );

  const isFormValid = () => sellAmount && buyAmount && sellToken && buyToken;

  const hasBalanceError = useMemo(
    () =>
      isSellAmountExceedingBalance() ||
      isNetworkFeeExceedingNativeBalance() ||
      isSellAmountOnlyExceedingBalance(),
    [
      isSellAmountExceedingBalance,
      isSellAmountOnlyExceedingBalance,
      isNetworkFeeExceedingNativeBalance,
    ]
  );

  const validationErrors = useMemo(
    () => SwapValidator.validateSwap(validationParams),
    [validationParams]
  );

  const hasInsufficientNativeFeeError = useMemo(
    () =>
      validationErrors.some(
        error =>
          error.errorType === "insufficient_native_fee" ||
          error.errorType === "insufficient_multiple_native_fees"
      ),
    [validationErrors]
  );

  const handlePreviewClick = async () => {
    const isValid = await trigger(["sellAmount", "buyAmount"]);

    if (!isValid || !isFormValid()) {
      return;
    }

    if (validationErrors.length > 0) {
      const firstError = validationErrors[0];
      toast({
        description: firstError.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      return;
    }

    props.onPreviewClick({
      buyToken,
      sellToken,
      sellAmount,
      buyAmount: isFastWithdrawal && fastWithdrawalBuyAmount ? fastWithdrawalBuyAmount : buyAmount,
      buyFiatAmount: formatCurrency(
        buyToken && buyAmount
          ? getTokenUsdPrice(
              buyToken.symbol,
              isFastWithdrawal && fastWithdrawalBuyAmount ? fastWithdrawalBuyAmount : buyAmount
            )
          : 0
      ),
      fiatValue: formatCurrency(buyToken && buyAmount ? getTokenUsdPrice(buyToken.symbol, 1) : 0),
      sellFiatAmount: formatCurrency(
        sellToken && sellAmount ? getTokenUsdPrice(sellToken.symbol, sellAmount) : 0
      ),
      slippage,
      zeroXQuote:
        selectedQuote?.provider === "0x" ? (selectedQuote.quote as ZeroXQuoteResponse) : null,
      gaslessQuote:
        selectedQuote?.provider === "Gasless"
          ? (selectedQuote.quote as GaslessQuoteResponse)
          : null,
      lifiQuote:
        selectedQuote?.provider === "LiFi" ? (selectedQuote.quote as LiFiQuoteResponse) : null,
      selectedQuote: selectedQuote as any,
      isGaslessSwap,
      isFastWithdrawal,
    });
  };

  const direction = useMemo(() => {
    if (!sellToken || !buyToken) {
      return BridgeDirection.EthereumToEtherlink; // Default value when tokens are not defined
    }
    try {
      return calculateDirection(sellToken, buyToken);
    } catch (error) {
      console.warn("Failed to calculate direction:", error);

      return BridgeDirection.EthereumToEtherlink; // Default value when calculation fails
    }
  }, [sellToken, buyToken]);

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form onSubmit={handleSubmit(() => {})}>
          <FormPageHeader title="Swap" />
          <Flex
            alignItems="center"
            justifyContent="center"
            flex="0 0 auto"
            width="full"
            marginRight="12px"
            marginBottom="32px"
          ></Flex>
          <ModalBody>
            <FormControl isInvalid={!!errors.sellAmount || !!errors.buyAmount || hasBalanceError}>
              <Flex justifyContent="space-between" marginBottom="3">
                <Text color={color("900")} fontSize="lg" fontWeight="600">
                  Sell
                </Text>
                <Text color={sellToken ? color("500") : color("300")} fontSize="md">
                  {sellToken ? "Network: " : "Network"}
                  {sellToken && (
                    <Text as="span" fontWeight="bold" textTransform="capitalize">
                      {sellTokenChainId}
                    </Text>
                  )}
                </Text>
              </Flex>
              <InputGroup>
                <Input
                  color={hasBalanceError ? color("red") : color("900")}
                  borderColor={hasBalanceError ? "red.500" : undefined}
                  isDisabled={false}
                  step="0.01"
                  type="number"
                  {...register("sellAmount", {
                    required: "Amount is required",
                    onChange: handleSellAmountChange,
                  })}
                  defaultValue={sellAmount}
                  onClick={() => {
                    if (!sellToken && props.onSelectSellTokenClick) {
                      props.onSelectSellTokenClick({ buyToken: buyToken });
                    }
                  }}
                  placeholder="0"
                />
                <InputRightElement width="auto" paddingRight="2">
                  <ButtonGroup>
                    <Button
                      paddingLeft="8px"
                      leftIcon={
                        sellToken ? (
                          sellToken?.iconUrl ? (
                            <TokenIconWrapper>
                              <Image
                                width="24px"
                                height="24px"
                                borderRadius="full"
                                alt={sellToken.symbol}
                                fallbackSrc="./static/coin-front.svg"
                                src={sellToken.iconUrl}
                              />
                            </TokenIconWrapper>
                          ) : (
                            <ChainIcon boxSize="24px" chainId={sellTokenChainId} />
                          )
                        ) : undefined
                      }
                      onClick={() => {
                        if (props.onSelectSellTokenClick) {
                          props.onSelectSellTokenClick({ buyToken: buyToken });
                        }
                      }}
                      rightIcon={<Icon as={ChevronDownIcon} marginInlineStart={0} />}
                      size="sm"
                      variant="ghost"
                    >
                      {sellToken ? sellToken.symbol : "Select Token"}
                    </Button>
                  </ButtonGroup>
                </InputRightElement>
              </InputGroup>
              {errors.sellAmount && (
                <FormErrorMessage data-testid="amount-error">
                  {errors.sellAmount.message}
                </FormErrorMessage>
              )}
              <Flex
                alignItems="center"
                justifyContent="space-between"
                marginTop="3"
                marginBottom="2"
              >
                <Flex alignItems="center" gap="2">
                  <Text color={color("500")}>
                    {formatCurrency(
                      sellToken && sellAmount ? getTokenUsdPrice(sellToken.symbol, sellAmount) : 0
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" gap="2">
                  <WalletIcon
                    width="18px"
                    height="18px"
                    color={hasBalanceError ? color("red") : color("400")}
                  />
                  <Text color={hasBalanceError ? "red.500" : color("500")}>
                    {sellToken ? Number(sellToken.balance).toFixed(6) : 0}
                  </Text>
                </Flex>
              </Flex>
            </FormControl>

            <Box position="relative" marginY={4}>
              <Flex position="relative" alignItems="center" width="100%">
                <Box flex="1" borderColor={color("100")} borderBottom="1px solid" />
                <Button
                  padding="3"
                  borderRadius="full"
                  backgroundColor={color("100")}
                  isDisabled={!sellToken || !buyToken}
                  marginX={3}
                  onClick={handleSwapTokensClick}
                  variant="iconButtonSolid"
                >
                  {isLoadingQuote ? (
                    <Spinner
                      width="18px"
                      height="18px"
                      color={color("400")}
                      transform="rotate(90deg)"
                    />
                  ) : (
                    <Icon
                      as={SwapIcon}
                      width="18px"
                      height="18px"
                      color={color("600")}
                      transform="rotate(90deg)"
                    />
                  )}
                </Button>
                <Box flex="1" borderColor={color("100")} borderBottom="1px solid" />
              </Flex>
            </Box>

            <Flex justifyContent="space-between" marginBottom="3">
              <Text color={color("900")} fontSize="lg" fontWeight="600">
                Buy
              </Text>
              <Text color={buyToken ? color("500") : color("300")} fontSize="md">
                {buyToken ? "Network: " : "Network"}
                {buyToken && (
                  <Text as="span" fontWeight="bold" textTransform="capitalize">
                    {buyTokenChainId}
                  </Text>
                )}
              </Text>
            </Flex>
            <FormControl isInvalid={!!errors.buyAmount}>
              <InputGroup>
                <Input
                  readOnly={true}
                  step="0.01"
                  type="number"
                  {...register("buyAmount", {
                    required: "Amount is required",
                  })}
                  onClick={() => {
                    if (!buyToken && props.onSelectBuyTokenClick) {
                      props.onSelectBuyTokenClick({ sellToken: sellToken });
                    }
                  }}
                  placeholder="0"
                />
                <InputRightElement width="auto" paddingRight="2">
                  <ButtonGroup>
                    <Button
                      paddingLeft="8px"
                      leftIcon={
                        buyToken ? (
                          buyToken.iconUrl ? (
                            <TokenIconWrapper>
                              <Image
                                width="24px"
                                height="24px"
                                borderRadius="full"
                                alt={buyToken.symbol}
                                fallbackSrc="./static/coin-front.svg"
                                src={buyToken.iconUrl}
                              />
                            </TokenIconWrapper>
                          ) : (
                            <ChainIcon boxSize="24px" chainId={buyTokenChainId} />
                          )
                        ) : undefined
                      }
                      onClick={() => {
                        if (props.onSelectBuyTokenClick) {
                          props.onSelectBuyTokenClick({ sellToken: sellToken });
                        }
                      }}
                      rightIcon={<Icon as={ChevronDownIcon} marginInlineStart={0} />}
                      size="sm"
                      variant="ghost"
                    >
                      {buyToken ? buyToken.symbol : "Select Token"}
                    </Button>
                  </ButtonGroup>
                </InputRightElement>
              </InputGroup>
              {errors.buyAmount && (
                <FormErrorMessage data-testid="buy-amount-error">
                  {errors.buyAmount.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              marginTop="3"
              paddingBottom="30px"
            >
              <Flex alignItems="center" gap="2">
                <Text color={color("500")}>
                  {formatCurrency(
                    buyToken && buyAmount ? getTokenUsdPrice(buyToken.symbol, buyAmount) : 0
                  )}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="2">
                <WalletIcon width="18px" height="18px" color={color("400")} />
                <Text color={color("500")}>
                  {buyToken ? Number(buyToken.balance).toFixed(6) : 0}
                </Text>
              </Flex>
            </Flex>

            {/* Gasless Swap Checkbox - only show for Ethereum swaps and not for native token swaps */}
            {selectedQuote?.provider === "0x" && !isNativeTokenSwap && (
              <Box marginBottom="4">
                <Checkbox
                  colorScheme="blue"
                  isChecked={isGaslessSwap}
                  onChange={handleGaslessSwapChange}
                  size="md"
                >
                  <Text color={color("700")} fontSize="sm">
                    Gasless swap
                  </Text>
                </Checkbox>
                <Text marginTop="1" color={color("500")} fontSize="xs">
                  Execute the swap without spending native tokens for gas.
                </Text>
              </Box>
            )}

            {/* Fast Withdrawal Checkbox */}
            {canShowFastWithdrawal && fastWithdrawalRate !== null && (
              <Box marginBottom="4">
                <Checkbox
                  colorScheme="blue"
                  isChecked={isFastWithdrawal}
                  onChange={e => setIsFastWithdrawal(e.target.checked)}
                  size="md"
                >
                  <Text color={color("700")} fontSize="sm">
                    Fast withdrawal (receive XTZ on Tezos in minutes)
                  </Text>
                </Checkbox>
                {isFastWithdrawal && (
                  <Text marginTop="1" color={color("500")} fontSize="xs">
                    {fastWithdrawalLoading ? (
                      "Loading fast withdrawal info..."
                    ) : fastWithdrawalError ? (
                      `Error: ${fastWithdrawalError}`
                    ) : (
                      <>
                        Min: {fastWithdrawalMin ?? "-"} XTZ | Max: {fastWithdrawalMax ?? "-"} XTZ |
                        Fee:{" "}
                        {fastWithdrawalRate ? (fastWithdrawalRate * 100).toFixed(2) + "%" : "-"}
                      </>
                    )}
                  </Text>
                )}
                {SwapValidator.getFastWithdrawalAmountError(validationParams) && (
                  <Text marginTop="1" color="red.500" fontSize="xs">
                    {SwapValidator.getFastWithdrawalAmountError(validationParams)}
                  </Text>
                )}
              </Box>
            )}

            {isLoadingQuote ? (
              <Skeleton
                width="100%"
                height={isNativeTokenSwap || !isGaslessSwap ? "88px" : "56px"}
                borderRadius="8px"
                endColor={color("200")}
                startColor={color("100")}
              />
            ) : canShowSwapInfo ? (
              <SwapInfo
                direction={direction}
                buyToken={buyToken}
                buyTokenSymbol={buyToken.symbol}
                conversionRate={conversionRate}
                defaultSlippage={0.5}
                fiatValue={formatCurrency(
                  buyToken && buyAmount ? getTokenUsdPrice(buyToken.symbol, 1) : 0
                )}
                layerZeroQuote={
                  selectedQuote?.provider === "LayerZero" ? (selectedQuote.quote as any) : null
                }
                lifiQuote={
                  selectedQuote?.provider === "LiFi"
                    ? (selectedQuote.quote as LiFiQuoteResponse)
                    : null
                }
                onFeeEstimateChange={handleFeeEstimateChange}
                onSlippageChange={handleSlippageChange}
                priceImpact={-0.05}
                // We are swapping tokens on high liquidity pools,
                // 0x suggest to not tweak slippage and price impact in this case
                provider={selectedQuote?.provider}
                sellToken={sellToken}
                sellTokenSymbol={sellToken.symbol}
                showNetworkFee={(!isTezosEtherlinkSwap && !isGaslessSwap) || isNativeTokenSwap}
                showPriceImpactAndSlippage={false}
                zeroXQuote={
                  selectedQuote?.provider === "0x"
                    ? (selectedQuote.quote as ZeroXQuoteResponse)
                    : null
                }
              />
            ) : null}
          </ModalBody>
          <ModalFooter>
            <FormSubmitButton
              isDisabled={false}
              isLoading={false}
              onClick={handlePreviewClick}
              title={hasInsufficientNativeFeeError ? "Not enough for gas" : "Preview"}
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
